import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  StarIcon, 
  CalendarDaysIcon,
  CpuChipIcon,
  PhotoIcon,
  CheckCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabaseClient';
import LoadingSpinner from '../components/LoadingSpinner';
import { format, subDays, startOfDay, endOfDay, parseISO } from 'date-fns';

interface ScanStats {
  totalScans: number;
  savedItems: number;
  thisMonth: number;
  thisWeek: number;
  todayScans: number;
  successRate: number;
  avgProcessingTime: number;
  mostUsedProvider: string;
  favoriteCount: number;
}

interface ChartData {
  date: string;
  scans: number;
  saved: number;
}

interface ProviderStats {
  provider: string;
  count: number;
  successRate: number;
  avgProcessingTime: number;
}

const AnalyticsPage: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<ScanStats>({
    totalScans: 0,
    savedItems: 0,
    thisMonth: 0,
    thisWeek: 0,
    todayScans: 0,
    successRate: 0,
    avgProcessingTime: 0,
    mostUsedProvider: 'N/A',
    favoriteCount: 0
  });
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [providerStats, setProviderStats] = useState<ProviderStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<'7days' | '30days' | '90days'>('30days');

  useEffect(() => {
    if (user) {
      loadAnalytics();
    }
  }, [user, selectedPeriod]);

  // Add effect to reload data when component becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        console.log('Page became visible, reloading analytics...');
        loadAnalytics();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  // Add effect to reload data when returning to this page
  useEffect(() => {
    const handleFocus = () => {
      if (user) {
        console.log('Window gained focus, reloading analytics...');
        loadAnalytics();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user]);

  // Set up real-time subscription for scans table changes
  useEffect(() => {
    if (!user) return;

    const subscription = supabase
      .channel('scans_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'scans',
          filter: `user_id=eq.${user.id}`
        }, 
        (payload) => {
          console.log('Analytics: Real-time update received', payload);
          // Delay refresh to ensure data is consistent
          setTimeout(() => {
            loadAnalytics();
          }, 500);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [user]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      
      // Small delay to ensure database has time to sync
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const now = new Date();
      const daysAgo = selectedPeriod === '7days' ? 7 : selectedPeriod === '30days' ? 30 : 90;
      const startDate = subDays(now, daysAgo);
      
      console.log('Analytics: Loading fresh data at', new Date().toISOString());
      
      // Load basic scan statistics - using same table as SavedPage
      const { data: scansData, error: scansError } = await supabase
        .from('scans')
        .select('*, is_saved')
        .eq('user_id', user!.id)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });

      console.log('Scans Query Result:', { scansData, scansError });

      if (scansError) throw scansError;

      // Load all-time stats - using same table as SavedPage
      const { data: allScansData, error: allScansError } = await supabase
        .from('scans')
        .select('*, is_saved')
        .eq('user_id', user!.id);

      console.log('All Scans Query Result:', { allScansData, allScansError });

      if (allScansError) throw allScansError;

      // Calculate statistics
      const totalScans = allScansData?.length || 0;
      
      // Try different approaches to count favorite items - using is_saved field
      const savedItemsMethod1 = allScansData?.filter(scan => scan.is_saved === true).length || 0;
      const savedItemsMethod2 = allScansData?.filter(scan => scan.is_saved == true).length || 0;
      const savedItemsMethod3 = allScansData?.filter(scan => scan.is_saved).length || 0;
      
      const savedItems = savedItemsMethod1;
      
      // Debug logging
      console.log('Analytics Debug - Full Details:', {
        totalScans,
        savedItemsMethod1,
        savedItemsMethod2,
        savedItemsMethod3,
        sampleScans: allScansData?.slice(0, 3),
        favoriteScans: allScansData?.filter(scan => scan.is_saved === true),
        allScansData: allScansData,
        userId: user!.id
      });
      
      const thisMonth = scansData?.filter(scan => {
        const scanDate = parseISO(scan.created_at);
        return scanDate >= subDays(now, 30);
      }).length || 0;
      const thisWeek = scansData?.filter(scan => {
        const scanDate = parseISO(scan.created_at);
        return scanDate >= subDays(now, 7);
      }).length || 0;
      const todayScans = scansData?.filter(scan => {
        const scanDate = parseISO(scan.created_at);
        return scanDate >= startOfDay(now);
      }).length || 0;

      // Calculate provider statistics
      const providerCounts: Record<string, { count: number; processingTimes: number[] }> = {};
      allScansData?.forEach(scan => {
        const provider = scan.api_provider || 'Unknown';
        if (!providerCounts[provider]) {
          providerCounts[provider] = { count: 0, processingTimes: [] };
        }
        providerCounts[provider].count++;
        
        // Mock processing time for now (in real app, this would be stored)
        const mockProcessingTime = Math.random() * 5000 + 1000; // 1-6 seconds
        providerCounts[provider].processingTimes.push(mockProcessingTime);
      });

      const providerStatsArray: ProviderStats[] = Object.entries(providerCounts).map(([provider, data]) => ({
        provider,
        count: data.count,
        successRate: Math.random() * 20 + 80, // Mock success rate 80-100%
        avgProcessingTime: data.processingTimes.reduce((a, b) => a + b, 0) / data.processingTimes.length
      })).sort((a, b) => b.count - a.count);

      const mostUsedProvider = providerStatsArray[0]?.provider || 'N/A';
      const avgProcessingTime = providerStatsArray.reduce((sum, p) => sum + p.avgProcessingTime, 0) / providerStatsArray.length || 0;

      // Generate chart data for the selected period
      const chartDataArray: ChartData[] = [];
      for (let i = daysAgo - 1; i >= 0; i--) {
        const date = subDays(now, i);
        const dayScans = scansData?.filter(scan => {
          const scanDate = parseISO(scan.created_at);
          return scanDate >= startOfDay(date) && scanDate <= endOfDay(date);
        }) || [];
        
        chartDataArray.push({
          date: format(date, 'M/d'),
          scans: dayScans.length,
          saved: dayScans.filter(scan => scan.is_favorite === true).length
        });
      }

      setStats({
        totalScans,
        savedItems,
        thisMonth,
        thisWeek,
        todayScans,
        successRate: Math.random() * 10 + 90, // Mock success rate
        avgProcessingTime,
        mostUsedProvider,
        favoriteCount: savedItems
      });

      setChartData(chartDataArray);
      setProviderStats(providerStatsArray);
      
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatProcessingTime = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ElementType;
    color: string;
    change?: { value: number; trend: 'up' | 'down' };
  }> = ({ title, value, icon: Icon, color, change }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          {change && (
            <div className={`flex items-center mt-1 text-sm ${
              change.trend === 'up' ? 'text-green-600' : 'text-red-600'
            }`}>
              {change.trend === 'up' ? (
                <ArrowUpIcon className="h-4 w-4 mr-1" />
              ) : (
                <ArrowDownIcon className="h-4 w-4 mr-1" />
              )}
              {Math.abs(change.value)}%
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
              <ChartBarIcon className="h-8 w-8 mr-3 text-indigo-600" />
              Analytics Dashboard
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Track your scanning activity and analyze your usage patterns
            </p>
          </div>
          <button
            onClick={() => loadAnalytics()}
            disabled={loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Loading...
              </>
            ) : (
              <>
                <ArrowUpIcon className="h-4 w-4 mr-2" />
                Refresh
              </>
            )}
          </button>
        </div>
      </div>

      {/* Period Selection */}
      <div className="mb-6">
        <div className="flex space-x-2">
          {[
            { key: '7days', label: 'Last 7 Days' },
            { key: '30days', label: 'Last 30 Days' },
            { key: '90days', label: 'Last 90 Days' }
          ].map(period => (
            <button
              key={period.key}
              onClick={() => setSelectedPeriod(period.key as any)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedPeriod === period.key
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-tour="tour-analytics-stats">
        <StatCard
          title="Total Scans"
          value={stats.totalScans}
          icon={PhotoIcon}
          color="bg-blue-500"
          change={{ value: 12, trend: 'up' }}
        />
        <StatCard
          title="Favorites"
          value={stats.savedItems}
          icon={StarIcon}
          color="bg-yellow-500"
          change={{ value: 8, trend: 'up' }}
        />
        <StatCard
          title="This Month"
          value={stats.thisMonth}
          icon={CalendarDaysIcon}
          color="bg-green-500"
          change={{ value: 15, trend: 'up' }}
        />
        <StatCard
          title="Success Rate"
          value={`${stats.successRate.toFixed(1)}%`}
          icon={CheckCircleIcon}
          color="bg-emerald-500"
          change={{ value: 2, trend: 'up' }}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Activity Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Daily Activity
          </h3>
          <div className="h-64">
            {chartData.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center text-gray-500 dark:text-gray-400">
                  <ChartBarIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No activity data available</p>
                  <p className="text-sm">Start scanning to see your activity</p>
                </div>
              </div>
            ) : (
              <div className="h-full">
                <div className="flex h-48 items-end space-x-1 mb-4">
                  {chartData.map((day, index) => {
                    const maxScans = Math.max(...chartData.map(d => d.scans), 1);
                    const barHeight = maxScans > 0 ? (day.scans / maxScans) * 180 : 2;
                    
                    return (
                      <div key={index} className="flex-1 flex flex-col items-center">
                        <div
                          className="w-full bg-indigo-500 rounded-t hover:bg-indigo-600 transition-colors cursor-pointer"
                          style={{ height: `${barHeight}px`, minHeight: '2px' }}
                          title={`${day.scans} scans on ${day.date}`}
                        />
                        <div className="w-full h-1 bg-gray-200 dark:bg-gray-700" />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                  {chartData.map((day, index) => (
                    <div key={index} className="flex-1 text-center">
                      {index % 3 === 0 ? day.date : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Provider Stats */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Provider Usage
          </h3>
          <div className="space-y-4">
            {providerStats.slice(0, 5).map((provider) => (
              <div key={provider.provider} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-2 h-2 rounded-full bg-indigo-500 mr-3" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {provider.provider}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {provider.count} scans
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {formatProcessingTime(provider.avgProcessingTime)} avg
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Quick Stats
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Today</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {stats.todayScans} scans
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">This Week</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {stats.thisWeek} scans
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Avg Processing</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {formatProcessingTime(stats.avgProcessingTime)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Most Used</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {stats.mostUsedProvider}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Performance
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-600 dark:text-gray-400">Success Rate</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {stats.successRate.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${stats.successRate}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-600 dark:text-gray-400">Favorites Rate</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {stats.totalScans > 0 ? ((stats.savedItems / stats.totalScans) * 100).toFixed(1) : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${stats.totalScans > 0 ? (stats.savedItems / stats.totalScans) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Insights
          </h3>
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <ArrowTrendingUpIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-900 dark:text-white font-medium">
                  Great Progress!
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Your scanning activity increased by 15% this month
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CpuChipIcon className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-900 dark:text-white font-medium">
                  Top Provider
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {stats.mostUsedProvider} is your most reliable choice
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <StarIcon className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-900 dark:text-white font-medium">
                  Quality Scans
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {((stats.savedItems / stats.totalScans) * 100).toFixed(0)}% of your scans are saved as favorites
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
